import { Loader2, LockKeyhole } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { CurveEditor, type Zoom } from "@/components/charts/CurveEditor";
import { defaultEventViewEnd, VIEW_ENDS, zoomFor } from "@/components/charts/chart-utils";
import { ChartViewControl, KeepTotalSwitch, OpenEditorButton } from "@/components/charts/ChartToolbar";
import { ChartModal, ChartPreview, useModalChartHeight } from "@/components/charts/ChartModal";
import { padded } from "@/api/adapt";
import type { MatchMarketPreviewDto, NodeResultsDto, RepairedRungDto } from "@/api/types";
import { useEventStated, useMarket } from "@/hooks/stated";
import { useActiveScenario, useBase, useNodeResults } from "@/hooks/world";
import { fmtValue, pct } from "@/lib/format";
import { fmtByDate, fmtDate, fmtMonth, HORIZON, idx, N, sortMonths } from "@/lib/months";
import { at, chanceAt, heroDate } from "@/lib/results";
import { toastUndo } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ancestors, drivers } from "@/model/graph";
import { useCanEdit } from "@/model/access";
import { useStore, useTerm } from "@/model/store";
import type { EventNode, MetricNode } from "@/model/types";
import { ConditionEditor } from "./ConditionEditor";
import { EventBaselineTable } from "./EventBaselineTable";
import { NodeScenarioStrip, ScenarioBar } from "@/components/scenarios/Scenarios";
import { NodeHeader } from "./NodeHeader";
import { HappenedLine, HERO_NUMBER, HeroDateSelect, ResolvePanel, Stat, TensionBadge, usePreviewHeight } from "./Hero";
import { AdminGroup, HistoryPanel, LinksPanel, NextStepCallout, Section, useIsolated } from "./NodeSide";
import { WhatMattersMost } from "./WhatMattersMost";
import { NodeIcon } from "@/components/NodeIcon";

/** Ladder months for the key-dates table: with more than three rungs, one column per quarter (its last ladder month). */
function tableLadderMonths(node: EventNode): string[] {
	if (!node.market) return [];
	const months = sortMonths([...new Set(node.market.rungs.map((r) => r.date.slice(0, 7)))]);
	if (node.market.rungs.length <= 3) return months;
	const byQuarter = new Map<string, string>();
	for (const m of months) byQuarter.set(`${m.slice(0, 4)}-Q${Math.ceil(Number(m.slice(5, 7)) / 3)}`, m);
	return [...byQuarter.values()];
}

/** 7.2: a derived event on a metric states that metric in its own unit, with the scenario against the baseline. */
function DerivedMetricLine({ node, hero, inScenario }: { node: EventNode; hero: string; inScenario: boolean }) {
	const nodes = useStore((s) => s.world.nodes);
	const { scenario } = useActiveScenario();
	const c = node.definition?.when.find((x): x is Extract<typeof x, { metric: string }> => "metric" in x);
	const m = c ? (nodes[c.metric] as MetricNode | undefined) : undefined;
	const res = useNodeResults(m?.kind === "metric" ? m.id : null, scenario?.id);
	if (!m || m.kind !== "metric") return null;
	const t = Math.min(N - 1, Math.max(0, idx(hero)));
	const likely = (run: NodeResultsDto["model"] | null | undefined) => (run?.series && "p50" in run.series ? at(run.series.p50, t) : undefined);
	const sv = res.data ? likely(res.data.model) : undefined;
	const bv = res.data ? (inScenario ? likely(res.data.without_scenario ?? res.data.model) : sv) : undefined;
	const u = m.unit;
	let text: string;
	if (inScenario) {
		if (sv === undefined || bv === undefined) text = "…";
		else {
			const d = sv - bv;
			const diff =
				u === "people"
					? Math.round(Math.abs(d)) === 0
						? null
						: `${Math.round(Math.abs(d))} ${d < 0 ? "fewer" : "more"}`
					: u === "%"
						? Math.round(Math.abs(d) * 100) === 0
							? null
							: `${Math.round(Math.abs(d) * 100)} points ${d < 0 ? "lower" : "higher"}`
						: Math.abs(d) < Math.abs(bv) * 0.005
							? null
							: `${fmtValue(Math.abs(d), u)} ${d < 0 ? "lower" : "higher"}`;
			text = `${fmtValue(sv, u)}, ${diff ? `${diff} than` : "the same as"} without the scenario`;
		}
	} else text = bv === undefined ? "…" : `likely ${fmtValue(bv, u)}`;
	return (
		<p className="mt-3 text-base">
			<Link to={`/node/${m.id}`} className="font-medium underline decoration-foreground/30 underline-offset-2 hover:decoration-foreground">
				<NodeIcon node={m} className="mr-1.5 text-muted-foreground" />
				{m.title}
			</Link>{" "}
			in {fmtMonth(hero)}: <span className="font-semibold tabular-nums">{text}</span>
		</p>
	);
}

export function EventView({ node }: { node: EventNode }) {
	const term = useTerm();
	const analyst = useStore((s) => s.analyst);
	const nodes = useStore((s) => s.world.nodes);
	const links = useStore((s) => s.world.links);
	const saveCurve = useStore((s) => s.saveCurve);
	const savePeriods = useStore((s) => s.savePeriods);
	const updateNode = useStore((s) => s.updateNode);
	const { scenario } = useActiveScenario();
	const upsertLever = useStore((s) => s.upsertLever);
	const upsertLeverPeriods = useStore((s) => s.upsertLeverPeriods);
	const res = useNodeResults(node.id, scenario?.id);
	const isolated = useIsolated(node);
	const previewHeight = usePreviewHeight();

	const [draft, setDraft] = useState<Record<string, number> | null>(null);
	const [mode, setMode] = useState<"curve" | "bars">("curve");
	const [keepTotal, setKeepTotal] = useState(false);
	const [editing, setEditing] = useState(false);
	const modalHeight = useModalChartHeight();
	const [hero, setHero] = useState(heroDate(node));

	const derived = !!node.definition;
	const canEdit = useCanEdit();
	// In a scenario the chart and table edit the scenario's own values for this event, starting from the baseline.
	const lever = scenario?.levers.find((l) => l.node === node.id);
	const scenBy = lever?.kind === "curve" ? lever.by : null;
	const inScenario = !!scenario;
	const shownBy = scenBy ?? node.by;
	const by = draft ?? shownBy;
	const [viewEnd, setViewEnd] = useState(() => defaultEventViewEnd(node.by, !!node.market));
	const [zoom, setZoom] = useState<Zoom>(() => zoomFor(defaultEventViewEnd(node.by, !!node.market)));

	// The curves come from the API: the stated baseline's exact series, the model's run, and the market.
	const stated = useEventStated(node, by, draft !== null, scenario?.id ?? null).F;
	const ownBaseline = useEventStated(node, node.by, false, scenario?.id ?? null).F;
	const market = useMarket(node);
	const model = res.data?.model;
	const modelF = useMemo(() => (model?.series && "chance_by" in model.series ? padded(model.series.chance_by) : null), [model]);
	const withoutRun = res.data ? (res.data.without_scenario ?? res.data.model) : null;
	const withoutF = useMemo(() => (withoutRun?.series && "chance_by" in withoutRun.series ? padded(withoutRun.series.chance_by) : null), [withoutRun]);
	// "Model" is the scenario's result while one is selected; "Without scenario" is Baseline's.
	const implied = modelF;
	const baseF = inScenario ? withoutF : modelF;
	const pending = res.pending;
	const paths = useBase().data?.paths;
	const hasDrivers = res.data?.has_drivers ?? drivers(node.id, nodes, links).length > 0;
	const ancestorIds = useMemo(() => [...ancestors(node.id, nodes, links)], [node.id, nodes, links]);
	const marketMonthly = market?.curve ?? null;
	const lastRung: RepairedRungDto | undefined = market?.rungs.at(-1);
	const lastRungMonth = lastRung?.date.slice(0, 7);

	// Headline numbers: the model's (baseline adjusted by drivers); with no drivers that is the baseline itself,
	// so the page shows one number rather than two that differ only by simulation noise (7.4).
	const modelLed = derived || hasDrivers;
	const main = modelLed ? implied : stated;
	const summary = model?.summary?.kind === "event" ? model.summary : null;
	const heroChance = main ? chanceAt(main, hero) : null;
	const baselineChance = chanceAt(stated, hero);
	const baselineModel = baseF ? chanceAt(baseF, hero) : null;
	const around = summary ? summary.around : undefined;
	const notBy = summary ? summary.not_by_horizon : null;
	const modelChance = implied ? chanceAt(implied, hero) : null;
	const heroMarket = marketMonthly && idx(hero) >= 0 ? (marketMonthly[Math.min(N - 1, idx(hero))] ?? null) : null;
	// §18.2 market_tension: own baseline more than 25 points from the market at its last rung.
	const tension =
		node.market && lastRung && lastRungMonth && !derived && Math.abs(chanceAt(ownBaseline, lastRungMonth) - lastRung.fair) > 0.25
			? { baseline: pct(chanceAt(ownBaseline, lastRungMonth)), market: pct(lastRung.fair), date: fmtMonth(lastRungMonth) }
			: null;

	function commit(nextBy: Record<string, number>) {
		setDraft(null);
		if (scenario) void upsertLever(scenario.id, { id: node.id, kind: "curve", node: node.id, by: nextBy });
		else void saveCurve(node.id, nextBy);
	}
	function commitPeriods(periods: Record<string, number>, z: Zoom) {
		return scenario ? upsertLeverPeriods(scenario.id, node.id, periods, z) : savePeriods(node.id, periods, z);
	}

	// "Use the market for the baseline": the API works out the new points (a dry run) for the confirmation copy.
	const matchMarket = useStore((s) => s.matchMarket);
	const applyMarketMatch = useStore((s) => s.applyMarketMatch);
	const [matchPlan, setMatchPlan] = useState<MatchMarketPreviewDto | null>(null);
	useEffect(() => {
		if (!node.market || derived) return;
		let alive = true;
		void matchMarket(node.id, true).then((p) => alive && setMatchPlan(p));
		return () => {
			alive = false;
		};
	}, [node.id, node.version, node.market, derived, matchMarket]);
	// Points after the last rung that the match lifts: when some sit below the last rung, the API rescales them into
	// the room above it, so they keep their shape (PRD §12.1, review A9).
	const changes =
		matchPlan?.rescaled && lastRungMonth
			? matchPlan.moved
					.filter((c) => idx(c.month) > idx(lastRungMonth) && Math.abs(c.after - c.before) > 0.0005)
					.map((c) => ({ month: c.month, from: c.before, to: c.after }))
			: [];
	async function matchToMarket() {
		if (!node.market || !lastRung) return;
		const lifted = changes.length > 0;
		if (!(await applyMarketMatch(node.id))) return;
		toastUndo(
			`Baseline now follows ${node.market.venue} to ${fmtDate(lastRung.date)}`,
			lifted ? `Your later points keep their shape, lifted to start above ${pct(lastRung.fair)}.` : "Your points after that are unchanged.",
		);
	}

	// Ladders put their action near-term: their month-ends join the key dates (PRD §10.2). The table collapses
	// long ladders to one column per quarter; the editor's table and the chart keep every rung.
	const ladderMonths = node.market ? [...new Set(node.market.rungs.map((r) => r.date.slice(0, 7)))] : [];
	const editorDates = sortMonths([...new Set([...node.key_dates, ...Object.keys(node.by), ...ladderMonths])]).filter((m) => idx(m) >= 0);
	const keyDates = sortMonths([...new Set([...node.key_dates, ...Object.keys(node.by), ...tableLadderMonths(node)])]).filter((m) => idx(m) >= 0);
	const summaryLabel = derived ? "Calculated" : modelLed ? "Model" : inScenario ? `${scenario!.name}` : "Baseline";
	const srSummary = main ? `${summaryLabel}: ${keyDates.map((m) => `${pct(chanceAt(main, m))} by ${fmtMonth(m)}`).join(", ")}.` : "";

	// The chart's legend is drawn under it by the chart itself; the toolbar holds the view controls.
	const toolbar = (inModal: boolean) => (
		<div className="ml-auto flex flex-wrap items-center gap-2">
			{pending && (
				<span className="flex items-center gap-1 text-xs text-muted-foreground">
					<Loader2 className="size-3.5 animate-spin" /> Updating…
				</span>
			)}
			<ToggleGroup type="single" size="sm" variant="outline" value={mode} onValueChange={(v) => v && setMode(v as "curve" | "bars")}>
				<ToggleGroupItem value="curve">{term("Chance by date", "CDF")}</ToggleGroupItem>
				<ToggleGroupItem value="bars">{term("When it happens", "PMF")}</ToggleGroupItem>
			</ToggleGroup>
			<ChartViewControl
				value={viewEnd}
				bars={mode === "bars"}
				onChange={(t, z) => {
					if (t === "fit") return;
					setViewEnd(t);
					setZoom(z);
				}}
			/>
			{mode === "bars" && !derived && <KeepTotalSwitch checked={keepTotal} onCheckedChange={setKeepTotal} />}
			{!inModal && <OpenEditorButton onOpen={() => setEditing(true)} readOnly={derived || !canEdit} />}
		</div>
	);
	const chart = (height: number, editable: boolean) => (
		<CurveEditor
			by={by}
			statedF={stated}
			onChange={setDraft}
			onCommit={commit}
			onCommitPeriods={commitPeriods}
			implied={implied}
			tone={inScenario ? "scenario" : "baseline"}
			statedLabel={inScenario ? `${scenario!.name}` : "Baseline"}
			ghost={inScenario ? baseF : null}
			ghostLabel="Without scenario"
			readOnly={derived || !editable || !canEdit}
			stated={!derived}
			height={height}
			viewEnd={viewEnd}
			mode={mode}
			zoom={zoom}
			keepTotal={keepTotal}
			market={node.market}
			rungs={market?.rungs}
			marketMonthly={marketMonthly}
			happened={node.happened}
			legend
		/>
	);

	const stats = (
		<>
			{modelLed && !derived && (
				<Stat
					label={inScenario ? `${scenario!.name}: values` : term("Baseline", "stated curve, before drivers")}
					value={pct(baselineChance)}
					tone={inScenario ? "scenario" : "baseline"}
				/>
			)}
			{modelLed && !derived && <Stat label={term("Model", "implied")} value={modelChance !== null ? pct(modelChance) : "…"} tone="model" pending={pending} />}
			{node.market && heroMarket !== null && <Stat label={`${node.market.venue}`} value={pct(heroMarket)} tone="market" />}
			{scenario && <Stat label="Without scenario" value={baselineModel !== null ? pct(baselineModel) : "…"} tone="ink" pending={pending} />}
		</>
	);
	const hasStats = (modelLed && !derived) || (node.market && heroMarket !== null) || !!scenario;

	return (
		<div>
			<NodeHeader
				node={node}
				share={{
					value: pct(heroChance),
					caption: `chance by ${fmtByDate(hero)}`,
					curve: main ? Array.from(main).slice(0, VIEW_ENDS[1].t + 1) : null,
					yRange: [0, 1],
				}}
				onForecast={derived || !canEdit ? undefined : () => setEditing(true)}
			/>

			<div className="grid gap-x-10 gap-y-12 xl:grid-cols-[minmax(0,1fr)_360px]">
				<div className="min-w-0">
					{/* Hero: the answer first (8.2). No card border; the chart below carries the only border. */}
					<section aria-label="Headline">
						<div className="flex flex-wrap items-end gap-x-10 gap-y-5">
							<div>
								<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
									<span className={HERO_NUMBER} aria-live="polite">
										{pct(heroChance)}
									</span>
									<span className="text-xl text-foreground/80">
										chance by <HeroDateSelect value={hero} dates={keyDates} onChange={setHero} />
									</span>
								</div>
								<div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-muted-foreground">
									<span>
										{around === undefined
											? "…"
											: around !== null
												? `${term("If it happens", "conditional median")}: around ${fmtMonth(around)}`
												: "Very unlikely to happen at all"}
									</span>
									<span>
										{pct(notBy)}
										{analyst && " (survival at horizon)"} it hasn’t happened by {HORIZON.slice(0, 4)}
									</span>
								</div>
							</div>
							{hasStats && <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">{stats}</div>}
						</div>
						{node.market && lastRung && lastRungMonth && heroMarket === null && (
							<p className="mt-3 text-sm text-muted-foreground">
								<span className="text-[var(--market)]">{node.market.venue}</span>: no view at {fmtMonth(hero)} · {pct(lastRung.fair)} by{" "}
								{fmtMonth(lastRungMonth)}
							</p>
						)}
						{tension && node.market && (
							<div className="mt-3">
								<TensionBadge baseline={tension.baseline} market={tension.market} date={tension.date} venue={node.market.venue} />
							</div>
						)}
						{derived && <DerivedMetricLine node={node} hero={hero} inScenario={inScenario} />}
						{node.happened && <HappenedLine nodeId={node.id} month={node.happened} />}
						{srSummary && <p className="sr-only">{srSummary}</p>}
					</section>

					{isolated && <NextStepCallout node={node} />}

					<div className="mt-10 mb-3">
						<ScenarioBar nodeIds={[node.id, ...ancestorIds]} />
					</div>
					{scenario && <NodeScenarioStrip node={node} scenario={scenario} />}

					{/* Chart: drag in place; "Open editor" for the full-screen editor and its table. */}
					<div className="rounded-xl border bg-card p-4 pt-3">
						<div className="mb-2 flex flex-wrap items-center gap-3">{toolbar(false)}</div>
						<ChartPreview onOpen={() => setEditing(true)} readOnly={derived || !canEdit} openButton={false}>
							{chart(previewHeight, true)}
						</ChartPreview>
					</div>

					{/* Key dates */}
					<div className="mt-8 overflow-x-auto">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b text-left text-muted-foreground">
									<th className="py-2 pr-4 font-normal">{term("Chance by", "P(T ≤ t)")}</th>
									{keyDates.map((m) => (
										<th key={m} className="px-3 py-2 text-right font-normal">
											<button
												className={cn(
													"rounded px-1 py-1 hover:bg-muted hover:text-foreground pointer-coarse:min-h-11 pointer-coarse:px-2",
													m === hero && "font-semibold text-foreground",
												)}
												aria-pressed={m === hero}
												title="Use as the headline date"
												onClick={() => setHero(m)}
											>
												{fmtMonth(m)}
											</button>
										</th>
									))}
								</tr>
							</thead>
							<tbody className="tabular-nums">
								{!derived && (
									<Row
										label={inScenario ? `${scenario!.name}: values` : "Baseline"}
										color={inScenario ? "var(--scenario)" : "var(--baseline)"}
										values={keyDates.map((m) => pct(chanceAt(stated, m)))}
										stated={keyDates.map((m) => m in by)}
									/>
								)}
								{modelLed && (
									<Row
										label={derived ? "Calculated" : "Model"}
										color="var(--model)"
										values={keyDates.map((m) => (implied ? pct(chanceAt(implied, m)) : "…"))}
									/>
								)}
								{scenario && <Row label="Without scenario" color="var(--ink)" values={keyDates.map((m) => (baseF ? pct(chanceAt(baseF, m)) : "…"))} />}
								{marketMonthly && node.market && (
									<Row
										label={node.market.venue}
										color="var(--market)"
										values={keyDates.map((m) => {
											const v = marketMonthly[Math.min(N - 1, idx(m))];
											return v === null || v === undefined ? NO_VIEW : pct(v);
										})}
									/>
								)}
							</tbody>
						</table>
						{analyst && (
							<p className="mt-2 text-xs text-muted-foreground">
								{derived ? "Calculated from its definition." : "Implied curve = baseline hazard adjusted by incoming multipliers and triggers."}{" "}
								{paths?.toLocaleString()} simulated futures.
							</p>
						)}
					</div>

					{derived && (
						<div className="mt-10 border-t pt-6">
							<div className="mb-4 flex items-start gap-2 text-sm">
								<LockKeyhole className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
								<p>
									This is calculated. To change it, drag one of its drivers:{" "}
									{drivers(node.id, nodes, links).map((id, i) => (
										<span key={id}>
											{i > 0 && ", "}
											<Link className="font-medium hover:underline" to={`/node/${id}`}>
												<NodeIcon node={nodes[id]} className="mr-1 text-muted-foreground" />
												{nodes[id].title}
											</Link>
										</span>
									))}
									.
								</p>
							</div>
							<ConditionEditor
								value={node.definition!}
								onChange={(d) => void updateNode(node.id, { definition: d })}
								nodes={Object.values(nodes)}
								selfId={node.id}
							/>
						</div>
					)}
				</div>

				<aside className="min-w-0" aria-label="Drivers and details">
					<LinksPanel node={node} />
					<Section title={`${term("What matters most", "tornado sensitivity")} by ${fmtByDate(hero)}`}>
						<WhatMattersMost nodeId={node.id} date={hero} upstream={ancestorIds.length} />
					</Section>
					{node.market && market && <MarketPanel node={node} rungs={market.rungs} onMatch={() => void matchToMarket()} changes={changes} />}
					<AdminGroup>
						{!node.happened && (
							<Section title="Resolution" quiet>
								<ResolvePanel nodeId={node.id} />
							</Section>
						)}
						<HistoryPanel node={node} />
					</AdminGroup>
				</aside>
			</div>

			<ChartModal
				open={editing}
				onOpenChange={(v) => {
					setEditing(v);
					setDraft(null);
				}}
				title={node.title}
				description={derived ? "The calculated chance by date, full screen." : "Edit the baseline chance by date by dragging the chart or typing in the table."}
				stats={
					<>
						{!derived && (
							<span className="text-[var(--baseline)]">
								Baseline <strong className="tabular-nums">{pct(baselineChance)}</strong>
							</span>
						)}
						<span className="text-[var(--model)]">
							{derived ? "Calculated" : "Model"} <strong className="tabular-nums">{modelChance !== null ? pct(modelChance) : "…"}</strong>
						</span>
						<span className="text-muted-foreground">by {fmtByDate(hero)}</span>
					</>
				}
			>
				<div className="mb-2 flex flex-wrap items-center gap-3">{toolbar(true)}</div>
				<div className="rounded-xl border bg-card p-4">{chart(modalHeight, true)}</div>
				{!derived && (
					<div className="mt-2 flex flex-wrap items-start gap-x-6 gap-y-2 text-xs text-muted-foreground">
						<p className="min-w-60 flex-1">
							{mode === "curve"
								? "This is the baseline: the chance on its own, before its drivers adjust it. Drag a point to change the chance by that date; drag the line sideways for sooner or later, up or down for more or less likely. Click the line to add a point; drag a point off the chart to remove it."
								: keepTotal
									? "Drag a bar to move chance between periods; the overall chance stays the same. Arrow keys work on a focused bar."
									: "Drag a bar to change the chance in that period; the difference comes from “not by” on the right. Drag that bar to change the overall chance."}
						</p>
					</div>
				)}
				<div className="mt-6 max-w-5xl">
					<EventBaselineTable
						by={by}
						statedF={stated}
						onCommit={commit}
						keyDates={editorDates}
						implied={implied}
						market={marketMonthly}
						marketLabel={node.market?.venue}
						valuesLabel={inScenario ? scenario!.name : "Baseline"}
						readOnly={derived || !canEdit}
					/>
				</div>
			</ChartModal>
		</div>
	);
}

function MarketPanel({
	node,
	rungs,
	onMatch,
	changes,
}: {
	node: EventNode;
	rungs: RepairedRungDto[];
	onMatch: () => void;
	changes: { month: string; from: number; to: number }[];
}) {
	const m = node.market!;
	const last = rungs.at(-1)!;
	const explain = changes.length
		? `Your points after ${fmtDate(last.date)} are below the market’s ${pct(last.fair)}. They keep their shape: each is lifted into the room above ${pct(
				last.fair,
			)}, so the curve still rises (${changes
				.slice(0, 3)
				.map((c) => `${fmtMonth(c.month)} ${pct(c.from)} → ${pct(c.to)}`)
				.join(", ")}${changes.length > 3 ? ", …" : ""}).`
		: `Your own points after ${fmtDate(last.date)} stay as they are.`;
	return (
		<Section title={`Market · ${m.venue}`}>
			<p className="mb-2 text-sm">
				“{m.title}” <span className="text-muted-foreground">as of {fmtDate(m.as_of)}</span>
			</p>
			<table className="w-full text-sm tabular-nums">
				<tbody>
					{rungs.map((r) => (
						<tr key={r.date} className="border-b last:border-0">
							<td className="py-1.5 pr-2">By {fmtDate(r.date)}</td>
							<td className="py-1.5 pr-2 text-right font-medium text-[var(--market)]">
								{pct(r.fair)}
								{r.adjusted && <span className="ml-1 text-xs font-normal text-muted-foreground">(was {pct(r.p)})</span>}
							</td>
							<td className="py-1.5 text-right text-xs text-muted-foreground">${Math.round(r.volume / 1000).toLocaleString()}k</td>
						</tr>
					))}
				</tbody>
			</table>
			<p className="mt-2 text-xs text-muted-foreground">
				Each rung asks “has it happened by this date?”, so the prices form a chance-by-date curve. The market has no view after {fmtDate(last.date)}; bigger
				diamonds traded more.
			</p>
			<Button size="sm" variant="outline" className="mt-3 h-auto min-h-8 text-left whitespace-normal" onClick={onMatch} title={explain}>
				Use the market for the baseline to {fmtDate(last.date)}
			</Button>
			<p className="mt-2 text-xs text-muted-foreground">{explain} Undo is one click away.</p>
		</Section>
	);
}

export { Stat };

const NO_VIEW = "no view";

function Row({ label, color, values, stated }: { label: string; color: string; values: string[]; stated?: boolean[] }) {
	return (
		<tr className="border-b last:border-0">
			<td className="py-2 pr-4 whitespace-nowrap" style={{ color }}>
				{label}
			</td>
			{values.map((v, i) => (
				<td key={i} className={cn("px-3 py-2 text-right", stated?.[i] && "font-semibold", v === NO_VIEW && "text-xs text-muted-foreground/60")}>
					{v}
				</td>
			))}
		</tr>
	);
}
